
import React, { useState } from 'react';
import { CreditCard, Lock, AlertCircle } from 'lucide-react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createPaymentIntent, confirmPayment } from './StripeWebhookHandler';
import './PaymentForm.css';

function PaymentForm({ orderData, onPaymentSuccess, onPaymentError, loading, setLoading }) {
  console.log('💳 PaymentForm rendered with orderData:', orderData);
  
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    console.log('🚀 PaymentForm handleSubmit called');
    
    if (!stripe || !elements) {
      console.log('❌ Stripe or Elements not ready');
      return;
    }

    setProcessing(true);
    setError(null);
    setLoading(true);
    console.log('⏳ Payment processing started...');

    try {
      // Create payment intent using our updated StripeWebhookHandler
      console.log('📋 Creating payment intent with auto-capture...');
      const paymentIntentData = {
        ...orderData,
        automatic_payment_methods: {
          enabled: true
        },
        capture_method: 'automatic_async',
        confirmation_method: 'automatic'
      };
      const clientSecret = await createPaymentIntent(paymentIntentData);
      console.log('✅ Payment intent created:', clientSecret);

      // Create payment method - customer will be created in CheckoutForm after payment
      console.log('💳 Creating payment method...');
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          name: orderData.customer.name,
          email: orderData.customer.email,
          phone: orderData.customer.phone,
          // Don't include address here so ZIP code field shows in card element
        },
      });

      if (paymentMethodError) {
        console.error('❌ Payment method error:', paymentMethodError);
        throw paymentMethodError;
      }

      console.log('✅ Payment method created:', paymentMethod.id);

      // Confirm payment with auto-capture
      console.log('💰 Confirming payment with auto-capture...');
      const paymentIntent = await confirmPayment(clientSecret, paymentMethod.id);
      console.log('✅ Payment confirmed with auto-capture:', paymentIntent);

      // Payment successful - customer will be created in CheckoutForm
      console.log('🎉 Payment successful, calling onPaymentSuccess');
      onPaymentSuccess({
        paymentIntent,
        paymentMethod,
        customer: null // Customer will be created in CheckoutForm
      });

    } catch (err) {
      console.error('❌ Payment error:', err);
      setError(err.message || 'Payment failed. Please try again.');
      onPaymentError(err);
    } finally {
      setProcessing(false);
      setLoading(false);
      console.log('🏁 Payment processing finished');
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: false, // Show ZIP code field!
  };

  return (
    <div className="payment-form">
      <div className="payment-header">
        <h3><CreditCard size={20} /> Payment Information</h3>
        <div className="security-notice">
          <Lock size={16} />
          <span>Secure payment powered by Stripe</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="payment-form-container">
        <div className="card-element-container">
          <label htmlFor="card-element">
            Card Details
          </label>
          <div className="card-wrapper">
            <CardElement 
              id="card-element" 
              options={cardElementOptions}
              className="card-element"
            />
          </div>
        </div>

        {error && (
          <div className="payment-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="payment-summary">
          <div className="summary-row">
            <span>Subtotal</span>
            <span>${orderData.subtotal.toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>
              {orderData.shipping?.type === 'pickup' 
                ? 'Pickup' 
                : `Shipping (${orderData.shipping?.method?.carrier || 'Standard'})`
              }
            </span>
            <span>
              {orderData.shipping?.cost === 0 
                ? 'FREE' 
                : `$${orderData.shipping?.cost.toFixed(2)}`
              }
            </span>
          </div>
          <div className="summary-row total">
            <span>Total</span>
            <span>${orderData.total.toFixed(2)}</span>
          </div>
        </div>

        <button 
          type="submit" 
          className="pay-button"
          disabled={!stripe || processing || loading}
        >
          {processing || loading ? (
            <>
              <div className="spinner"></div>
              Processing...
            </>
          ) : (
            <>
              <Lock size={16} />
              Pay ${orderData.total.toFixed(2)}
            </>
          )}
        </button>

        <div className="payment-disclaimer">
          <p>
            By completing this purchase, you agree to our Terms of Service and Privacy Policy. 
            Your payment information is encrypted and secure.
          </p>
        </div>
      </form>
    </div>
  );
}

export default PaymentForm;
